import { toast } from 'react-toastify'
import { useCallback, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import {
  InputWithValidationError,
  ResizibleTextArea,
  CheckboxWithDescription,
  SimpleCustomSelect,
  InlineList
} from 'components/FormInputs'
import { FormSection } from 'components/Form'
import { PageHeader } from 'components/PageHeader'
import { serviceProviders, deviceTypes } from 'components/FormInputs/options'

import { Product, ProductType } from 'models'
import { create } from '../services/api'

const productTypeOptions = [
  { value: ProductType.hardware, label: 'Hardware' },
  { value: ProductType.connectivity, label: 'Conectividade' },
  { value: ProductType.software, label: 'Software' }
]

export function Create() {
  const navigate = useNavigate()

  const [product, setProduct] = useState<Record<string, any>>({
    type: ProductType.hardware,
    active: false
  })
  const [subtype, setSubtype] = useState<string>(deviceTypes[0].value)
  const [nameError, setNameError] = useState<string | null>(null)

  const subtypeOptions =
    product.type === ProductType.connectivity ? serviceProviders : deviceTypes
  const showSubtype = product.type !== ProductType.software

  const onAttributeChange = useCallback(
    (attr: keyof Partial<Product>, value: any) => {
      if (attr === 'type') {
        setSubtype(
          value === ProductType.connectivity
            ? serviceProviders[0].value
            : deviceTypes[0].value
        )
      }
      setProduct((prev) => ({ ...prev, [attr]: value }))
    },
    []
  )

  const onSave = useCallback(async () => {
    if (!product.name?.trim()) {
      setNameError('O campo deve ser preenchido')
      return
    }
    setNameError(null)

    const meta =
      showSubtype && subtype ? { allowed_subtypes: [subtype] } : undefined

    try {
      await create({ ...product, meta })
      toast.success('Produto criado com sucesso')
      navigate('/products')
    } catch (err: any) {
      toast.error(
        err.suggestedMessage ?? 'Houve uma falha ao tentar criar o produto'
      )
    }
  }, [product, subtype, showSubtype, navigate])

  return (
    <>
      <PageHeader title="Produtos" action="Novo" />
      <div className="flex flex-row space-x-8">
        <div className="w-3/5 px-4 animate-fade-in-down">
          <form
            onSubmit={(e) => {
              e.preventDefault()
              onSave()
            }}
          >
            <FormSection title="Novo produto">
              <InlineList
                label="Tipo de produto"
                description=""
                options={productTypeOptions}
                value={product.type}
                onChange={(type) => onAttributeChange('type', type)}
              />

              <InputWithValidationError
                label="Nome"
                name="name"
                value={product.name ?? ''}
                onChange={(name) => onAttributeChange('name', name)}
                error={nameError}
              />

              <ResizibleTextArea
                label="Descrição"
                name="description"
                value={product.description ?? ''}
                onChange={(description) =>
                  onAttributeChange('description', description)
                }
              />

              <fieldset className="space-y-5">
                <legend className="text-left">Estado de disponibilidade</legend>
                <CheckboxWithDescription
                  label="Ativado"
                  name="active"
                  checked={product.active}
                  onChange={(active) => onAttributeChange('active', active)}
                  description="Ao ativar o produto, ele estará disponível para vincular a planos."
                />
              </fieldset>

              {showSubtype && (
                <SimpleCustomSelect
                  label="Restrição"
                  options={subtypeOptions}
                  value={subtype}
                  onChange={(value) => setSubtype(value)}
                />
              )}
            </FormSection>

            <div className="mt-12 flex justify-end">
              <button
                type="button"
                className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none"
                onClick={() => navigate(-1)}
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="ml-3 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-emerald-600 hover:bg-emerald-700 focus:outline-none"
              >
                Salvar
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  )
}
